/* eslint-disable @typescript-eslint/no-explicit-any */
import { get, pipe, set, isEqual } from "lodash/fp";
import { set as testSet } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { changeset } from "../../utils/object_utils";
import { ObjectSchema, ValidationError } from "yup";

interface E {
  message: string;
  path: string;
}

export class FormField<T> {
  value: T;
  errors: E[];
  set: (value: T) => void;
  touched: boolean;
  setTouched: (touched: boolean) => void;

  constructor({
    value,
    errors = [],
    set,
    touched,
    setTouched,
  }: {
    value: T;
    errors: E[];
    set: (value: T) => void;
    touched: boolean;
    setTouched: (touched: boolean) => void;
  }) {
    this.value = value;
    this.errors = errors;
    this.set = set;
    this.touched = touched;
    this.setTouched = setTouched;
  }
}

/*
 * This is a recursive, conditional type definition to support the following structure
   {
     a: FormField<string>,
     b: {
       c: FormField<number>,
       d: {
         e: FormField<number>
       }
     }
   }

   We are treating arrays as leaf nodes in this case so that we are able to call `set` on them as FormField (NOT FormFields).
   Note that this currently does not support typescript Tuple types.
   As of now we also can not have an object as a leaf node.
 */
export type FormFields<O> = {
  [T in keyof O]: O[T] extends Array<infer E>
    ? FormField<Array<E>>
    : O[T] extends Record<string, any>
      ? FormFields<O[T]>
      : FormField<O[T]>;
};

export interface FormHook<O> {
  changes: Partial<O>;
  form: FormFields<O>;
  hasErrors: boolean;
  setErrors: (errors: E[]) => void;
  values: O;
  validateForm: () => Promise<void>;
}

export type FormArrayHook<O extends { id: string }> = {
  forms: (Omit<FormHook<O>, "validateForm"> & { removeForm: () => void })[];
  addForm: (obj: O) => void;
  validateForms: () => Promise<void>;
};

function serialize<O>(
  obj: O,
  update: (path: string) => <T>(value: T) => void,
  currentPath = "",
  updateTouched: (path: string) => (touched: boolean) => void,
  useTouch?: boolean,
): FormFields<O> {
  return (Object.entries(obj as object) as [keyof O, any]).reduce((acc, [key, value]) => {
    const path = currentPath.length === 0 ? key : `${currentPath}.${key}`;
    if (typeof value === "object" && !Array.isArray(value)) {
      if (value === null) {
        acc[key] = new FormField({
          value: null,
          errors: [],
          set: update(path),
          touched: !useTouch,
          setTouched: updateTouched(path),
        });
      } else {
        acc[key] = { ...serialize(value, update, path, updateTouched, useTouch) };
      }
    } else {
      acc[key] = new FormField({
        value,
        errors: [],
        set: update(path),
        touched: !useTouch,
        setTouched: updateTouched(path),
      });
    }

    return acc;
  }, {});
}

function deserialize<O>(form: FormFields<O>): O {
  return (Object.entries(form) as [keyof O, any]).reduce((acc, [key, obj]) => {
    if (obj instanceof FormField) {
      acc[key] = obj.value;
    } else {
      acc[key] = deserialize(obj);
    }
    return acc;
  }, {} as O);
}

function resetErrors<O>(obj: O) {
  if (obj) {
    (Object.keys(obj) as (keyof O)[]).forEach((key) => {
      if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
        obj[key] = resetErrors(obj[key]);
      } else if (key === "errors") {
        obj[key] = [] as any;
      }
    });
    return obj;
  }
  return obj;
}

export default function useForm<O extends { [key: string]: any }>(
  obj: O,
  schema?: ObjectSchema<O>,
  useTouch?: boolean,
): FormHook<O> {
  function update(path: string) {
    return function <T>(value: T): void {
      const setFormWithoutErrors = () => {
        const newForm = pipe(set(`${path}.value`, value), set(`${path}.errors`, []));
        setForm(newForm);
      };

      if (schema) {
        setForm((oldForm) => {
          // If we're using touch, we want to carry the previous touched value forward across form
          // updates. If we are not using touch, set the touched value to true. This preserves
          // previous form behavior.
          const preservedTouchValue = useTouch ? (oldForm[path].touched as boolean) : true;

          try {
            schema.validateSync(
              testSet(deserialize(oldForm) as Record<string, unknown>, path, value),
              { abortEarly: false },
            );
            const newForm = pipe(
              set(`${path}.value`, value),
              set(`${path}.errors`, []),
              set(`${path}.touched`, preservedTouchValue),
            );
            return resetErrors(newForm(oldForm));
          } catch (errors: any) {
            // If useTouch is true, we only want to set the error on a field where there have been changes
            const errorSets = errors.inner.map((error: ValidationError) =>
              get(`${error.path}.touched`, oldForm)
                ? set(`${error.path}.errors`, [error.message])
                : set(`${error.path}.errors`, []),
            );
            const newForm = pipe(
              (oldForm) => resetErrors(oldForm),
              set(`${path}.value`, value),
              ...errorSets,
              set(`${path}.touched`, preservedTouchValue),
            );
            return newForm(oldForm);
          }
        });
      } else {
        setFormWithoutErrors();
      }
    };
  }

  function updateTouched(path: string) {
    return function (touched: boolean) {
      setForm((oldForm) => {
        const newForm = pipe((oldForm) => oldForm, set(`${path}.touched`, touched));
        return newForm(oldForm);
      });
    };
  }

  const validateForm = async () => {
    if (schema) {
      let formIsValid = false;
      await setForm((oldForm) => {
        const paths = Object.keys(oldForm);

        try {
          schema.validateSync(deserialize(oldForm), { abortEarly: false });
          formIsValid = true;
        } catch (errors: any) {
          const errorSets = errors.inner.map((error: ValidationError) =>
            set(`${error.path}.errors`, [error.message]),
          );
          const touchedSets = paths.map((path) => set(`${path}.touched`, true));
          const newForm = pipe((oldForm) => resetErrors(oldForm), ...errorSets, ...touchedSets);
          formIsValid = false;
          return newForm(oldForm);
        }

        return oldForm;
      });
      if (formIsValid) {
        return Promise.resolve();
      } else {
        throw new Error("Form cannot be submitted yet");
      }
    } else {
      return Promise.resolve();
    }
  };

  function setErrors(errors: E[]): void {
    const formWithErrors = errors.reduce((acc, error) => {
      const errors = get(`${error.path}.errors`, acc);
      return set(`${error.path}.errors`, (errors as Array<string>).concat([error.message]), acc);
    }, form);
    setForm(formWithErrors);
  }

  const [base, setBase] = useState(obj);
  const [form, setForm] = useState(serialize(obj, update, undefined, updateTouched, useTouch));
  const [changes, setChanges] = useState(changeset(deserialize(form) as object, obj as object));

  const values = useMemo(() => deserialize(form), [form]);
  useEffect(() => {
    // @TODO: seems like maybe instead of checking input reference we should instead return a function
    // to trigger this update? if this is derived from react props or another hook there's a good chance
    // it will be a new reference on every render, which means whatever is calling this usually has
    // to leverage useMemo to make sure we dont get stuck in a loop

    // check for deep equality - this should take care of the comment above but we may want to re-think this in future
    if (!isEqual(base, obj)) {
      setForm(serialize(obj, update, undefined, updateTouched, useTouch));
      setBase(obj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obj, base]);

  useEffect(() => {
    setChanges(changeset(deserialize(form) as object, base as object));
  }, [base, form]);

  function hasError(field: FormFields<O> | FormField<keyof O>): boolean {
    if (field instanceof FormField) {
      return field?.errors?.length > 0;
    } else {
      return (Object.values(field) as any).some(hasError);
    }
  }

  const hasErrors = (Object.values(form) as [keyof O, any]).some(hasError);

  return { changes, form, hasErrors, setErrors, values, validateForm };
}

// every "form" object needs an id so that we can track it in the list. if you're creating new objects
// on the client, consider using something like https://www.npmjs.com/package/react-id-generator or a date string
export function useFormArray<O extends { id: string }>(
  objs: O[],
  schema?: ObjectSchema<O>,
  useTouch?: boolean,
): FormArrayHook<O> {
  function updateFormArray(id: string) {
    return function update(path: string) {
      return function <T>(value: T): void {
        if (schema) {
          // run validation on the inputs before updating
          setState((oldState) => {
            const forms = oldState.forms.map((oldForm) => {
              const deserializedForm = deserialize(oldForm);
              const preservedTouchValue = useTouch ? oldForm[path as keyof O].touched : true;

              try {
                schema.validateSync(
                  oldForm.id.value === id
                    ? testSet(deserializedForm, path, value)
                    : deserializedForm,
                  { abortEarly: false },
                );

                const newForm = pipe(
                  set(`${path}.value`, value),
                  set(`${path}.errors`, []),
                  set(`${path}.touched`, preservedTouchValue),
                );
                return resetErrors(oldForm.id.value === id ? newForm(oldForm) : oldForm);
              } catch (errors: any) {
                const errorSets = errors.inner.map((error: ValidationError) =>
                  get(`${error.path}.touched`, oldForm)
                    ? set(`${error.path}.errors`, [error.message])
                    : set(`${error.path}.errors`, []),
                );

                const newFormWithErrors = pipe(
                  ...(oldForm.id.value === id ? [set(`${path}.value`, value)] : []),
                  (oldForm) => resetErrors(oldForm),
                  ...errorSets,
                );

                return newFormWithErrors(oldForm);
              }
            });
            return {
              forms,
              changeSets: forms.map((form) => {
                const obj = objs.find((obj) => form.id.value === obj.id);
                // if this is an object that was added, we just want the entire object
                return {
                  changeSet: obj ? changeset(deserialize(form), obj) : deserialize(form),
                  id: form.id.value,
                };
              }),
            };
          });
        } else {
          // no schema present, so we just update the values
          const newForm = pipe(set(`${path}.value`, value), set(`${path}.errors`, []));
          setState((oldState) => {
            const forms = oldState.forms.map((oldForm) =>
              oldForm.id.value === id ? newForm(oldForm) : oldForm,
            );
            return {
              forms,
              changeSets: forms.map((form) => {
                const obj = objs.find((obj) => form.id.value === obj.id);
                // if this is an object that was added, we just want the entire object
                return {
                  changeSet: obj ? changeset(deserialize(form), obj) : deserialize(form),
                  id: form.id.value,
                };
              }),
            };
          });
        }
      };
    };
  }

  function updateTouchedFormArray(id: string) {
    return function update(path: string) {
      return function (touched: boolean) {
        setState((oldState) => {
          const form = oldState.forms.find((form) => form.id.value === id);
          if (!form) {
            throw new Error("Failed to find form in form array");
          }
          const newForm = pipe((oldForm) => oldForm, set(`${path}.touched`, touched));

          return {
            ...oldState,
            forms: oldState.forms.map((oldForm) =>
              id === oldForm.id.value ? newForm(oldForm) : oldForm,
            ),
          };
        });
      };
    };
  }

  const validateForms = async () => {
    if (schema) {
      let formIsValid = true;
      await setState((oldState) => {
        const forms = oldState.forms.map((oldForm) => {
          const deserializedForm = deserialize(oldForm);
          const paths = Object.keys(deserializedForm);
          try {
            schema.validateSync(deserializedForm, { abortEarly: false });
            return oldForm;
          } catch (errors: any) {
            formIsValid = false;
            const errorSets = errors.inner.map((error: ValidationError) =>
              set(`${error.path}.errors`, [error.message]),
            );
            const touchedSets = paths.map((path) => set(`${path}.touched`, true));
            const newForm = pipe((oldForm) => resetErrors(oldForm), ...errorSets, ...touchedSets);
            return newForm(oldForm);
          }
        });

        return { ...oldState, forms };
      });
      if (formIsValid) {
        return Promise.resolve();
      } else {
        throw new Error("Form cannot be submitted yet");
      }
    } else {
      return Promise.resolve();
    }
  };

  function setErrorsFormArray(id: string) {
    return function setErrors(errors: E[]): void {
      const form = state.forms.find((form) => form.id.value === id);
      if (!form) {
        throw new Error("Failed to find form in form array");
      }
      const formWithErrors = errors.reduce((acc, error) => {
        const errors = get(`${error.path}.errors`, acc);
        return set(`${error.path}.errors`, errors.concat([error.message]), acc);
      }, form);
      setState((oldState) => ({
        ...oldState,
        forms: oldState.forms.map((oldForm) =>
          id === oldForm.id.value ? formWithErrors : oldForm,
        ),
      }));
    };
  }

  const [bases, setBases] = useState(objs);
  const serialized = objs.map((obj) =>
    serialize(obj, updateFormArray(obj.id), undefined, updateTouchedFormArray(obj.id), useTouch),
  );
  const [state, setState] = useState({
    forms: serialized,
    changeSets: serialized.map((form) => {
      const obj = objs.find((obj) => form.id.value === obj.id);
      // if this is an object that was added, we just want the entire object
      return {
        changeSet: obj ? changeset(deserialize(form), obj) : deserialize(form),
        id: form.id.value,
      };
    }),
  });

  const values = useMemo(() => state.forms.map((form) => deserialize(form)), [state]);
  useEffect(() => {
    if (bases !== objs) {
      const forms = objs.map((obj) =>
        serialize(
          obj,
          updateFormArray(obj.id),
          undefined,
          updateTouchedFormArray(obj.id),
          useTouch,
        ),
      );
      setState({
        forms,
        changeSets: forms.map((form) => {
          const obj = objs.find((obj) => form.id.value === obj.id);
          // if this is an object that was added, we just want the entire object
          return {
            changeSet: obj ? changeset(deserialize(form), obj) : deserialize(form),
            id: form.id.value,
          };
        }),
      });
      setBases(objs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objs, bases]);

  function hasError(field: FormFields<O> | FormField<keyof O>): boolean {
    if (field instanceof FormField) {
      return field?.errors?.length > 0;
    } else {
      return (Object.values(field) as any).some(hasError);
    }
  }

  return {
    validateForms,
    forms: state.forms.map((form) => {
      const hasErrors = (Object.values(form) as [keyof O, any]).some(hasError);
      const changes = state.changeSets.find((changeset) => changeset.id === form.id.value);
      const objectValues = values.find((value) => value.id === form.id.value);
      if (!changes || !objectValues) {
        throw new Error("Unable to find form in form array");
      }
      return {
        changes: changes.changeSet,
        form,
        hasErrors,
        setErrors: setErrorsFormArray(objectValues.id),
        values: objectValues,
        removeForm: () =>
          setState((oldState) => {
            const i = oldState.forms.findIndex((oldForm) => oldForm.id.value === objectValues.id);
            return {
              ...oldState,
              forms: [...oldState.forms.slice(0, i), ...oldState.forms.slice(i + 1)],
            };
          }),
      };
    }),
    addForm: (obj: O) =>
      setState((oldState) => {
        const forms = [
          ...oldState.forms,
          serialize(
            obj,
            updateFormArray(obj.id),
            undefined,
            updateTouchedFormArray(obj.id),
            useTouch,
          ),
        ];
        return {
          forms,
          changeSets: forms.map((form) => {
            const obj = objs.find((obj) => form.id.value === obj.id);
            // if this is an object that was added, we just want the entire object
            return {
              changeSet: obj ? changeset(deserialize(form), obj) : deserialize(form),
              id: form.id.value,
            };
          }),
        };
      }),
  };
}
